import React, { useState, useRef, useEffect } from 'react';
import { Army, ARMY_CONFIG } from '../armyConfig';
import { getArmyDisplayName, getArmyTriggerDisplayName } from '../utils/armyUtils';
import styles from './ArmySelect.module.css';

interface ArmyComboBoxProps {
  value: Army | undefined;
  onChange: (value: Army) => void;
  placeholder?: string;
  align?: 'left' | 'right';
}

interface GroupedArmy {
  grandAlliance: string;
  faction: string;
  army: Army;
  name: string;
}

const ArmyComboBox: React.FC<ArmyComboBoxProps> = ({ value, onChange, placeholder = 'Select army...', align = 'left' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Track if the device is likely to have a physical keyboard
  const [hasKeyboard, setHasKeyboard] = useState(true);

  // Detect if device is touch-only (no physical keyboard)
  useEffect(() => {
    // If window.ontouchstart exists, it's likely a touch device
    // But some laptops have touchscreens, so we also check for maxTouchPoints
    const isTouch =
      ('ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.userAgent.match(/Mobile|Android|iP(ad|hone)/i));
    setHasKeyboard(!isTouch);
  }, []);

  // Auto-focus input when dropdown opens (keyboard devices only)
  useEffect(() => {
    if (isOpen && hasKeyboard && inputRef.current) {
      inputRef.current.focus();
      // Optionally select all text
      inputRef.current.setSelectionRange(0, inputRef.current.value.length);
    }
  }, [isOpen, hasKeyboard]);

  // Keydown listener: if dropdown is open, input is not focused, and a printable key is pressed, focus input and forward the key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (!inputRef.current || document.activeElement === inputRef.current) return;
      // Only respond to printable keys (ignore control/meta/alt, arrows, etc)
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        inputRef.current.focus();
        // Forward the key to the input (simulate typing)
        // This is a hack: manually update the value and fire input event
        const start = inputRef.current.selectionStart ?? inputRef.current.value.length;
        const end = inputRef.current.selectionEnd ?? inputRef.current.value.length;
        const newValue =
          inputRef.current.value.slice(0, start) + e.key + inputRef.current.value.slice(end);
        setSearchTerm(newValue);
        // Move caret after inserted char
        setTimeout(() => {
          inputRef.current?.setSelectionRange(start + 1, start + 1);
        }, 0);
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Group and sort armies
  const groupedArmies = React.useMemo(() => {
    const armies: GroupedArmy[] = Object.entries(ARMY_CONFIG).map(([key, config]) => ({
      grandAlliance: config.grandAlliance,
      faction: config.faction,
      army: key as Army,
      name: config.name,
    }));
    const allianceOrder = ['Order', 'Chaos', 'Death', 'Destruction'];
    return armies.sort((a, b) => {
      const allianceOrderA = allianceOrder.indexOf(a.grandAlliance);
      const allianceOrderB = allianceOrder.indexOf(b.grandAlliance);
      if (allianceOrderA !== allianceOrderB) return allianceOrderA - allianceOrderB;
      if (a.faction !== b.faction) return a.faction.localeCompare(b.faction);
      return a.name.localeCompare(b.name);
    });
  }, []);

  // Filter and group armies based on search term
  const filteredArmiesByAlliance = React.useMemo(() => {
    const armies = !searchTerm
      ? groupedArmies
      : groupedArmies.filter(army => {
          const config = ARMY_CONFIG[army.army];
          const search = searchTerm.toLowerCase();
          return (
            army.faction.toLowerCase().includes(search) ||
            army.name.toLowerCase().includes(search) ||
            army.grandAlliance.toLowerCase().includes(search) ||
            (config.shortName && config.shortName.toLowerCase().includes(search)) ||
            (config.shortArmyName && config.shortArmyName.toLowerCase().includes(search))
          );
        });
    // Group by alliance and then by faction
    const grouped = new Map<string, Map<string, GroupedArmy[]>>();
    armies.forEach(army => {
      if (!grouped.has(army.grandAlliance)) grouped.set(army.grandAlliance, new Map());
      const allianceGroup = grouped.get(army.grandAlliance)!;
      if (!allianceGroup.has(army.faction)) allianceGroup.set(army.faction, []);
      allianceGroup.get(army.faction)!.push(army);
    });
    return grouped;
  }, [groupedArmies, searchTerm]);

  // Handle selection
  function handleSelect(army: Army) {
    onChange(army);
    setIsOpen(false);
    setSearchTerm('');
  }

  // Keyboard navigation (optional, for accessibility)
  // ...could be added here...

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {/* Trigger */}
      {!isOpen ? (
        <div
          className={styles.trigger}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setIsOpen(true)}
        >
          {value ? getArmyTriggerDisplayName(value) : <span style={{ color: '#888' }}>{placeholder}</span>}
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
          </span>
        </div>
      ) : (
        <div className={styles.trigger} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            ref={inputRef}
            className={styles.searchInput}
            style={{ flex: 1 }}
            placeholder="Search armies..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            // No autoFocus!
            onKeyDown={e => {
              if (e.key === 'Escape') {
                setIsOpen(false);
                setSearchTerm('');
              }
            }}
          />
          <span
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => {
              if (searchTerm) {
                setSearchTerm('');
                inputRef.current?.focus();
              } else {
                setIsOpen(false);
              }
            }}
          >
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </span>
        </div>
      )}
      {/* Dropdown */}
      {isOpen && (
        <div
          className={styles.content}
          style={{
            position: 'absolute',
            top: '100%',
            left: align === 'left' ? 0 : 'auto',
            right: align === 'right' ? 0 : 'auto',
            zIndex: 1000,
            maxHeight: 320,
            overflowY: 'auto',
            background: '#111',
            border: '1px solid #444',
            borderRadius: 6,
            marginTop: 2,
          }}
        >
          {filteredArmiesByAlliance.size === 0 ? (
            <div className={styles.noResults} style={{ padding: 12, color: '#888' }}>No armies found</div>
          ) : (
            Array.from(filteredArmiesByAlliance.entries()).map(([alliance, factions]) => (
              <div key={alliance}>
                <div className={styles.allianceLabel}>{alliance}</div>
                {Array.from(factions.entries()).map(([faction, armies], factionIdx) => {
                  const groupClass = factionIdx % 2 === 0 ? styles.factionGroupEven : styles.factionGroupOdd;
                  // If only one army in this group, render as top-level (not indented)
                  if (armies.length === 1) {
                    return (
                      <div key={faction} className={groupClass}>
                        <div
                          className={`${styles.item}${armies[0].army === value ? ' ' + styles.selectedItem : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSelect(armies[0].army)}
                        >
                          {getArmyDisplayName(armies[0].army)}
                        </div>
                      </div>
                    );
                  }
                  // Otherwise, render with heading and indented items
                  return (
                    <div key={faction} className={groupClass}>
                      <div className={styles.factionLabel}>{faction}</div>
                      {armies.map(army => (
                        <div
                          key={army.army}
                          className={`${styles.item} ${styles.indentedItem}${army.army === value ? ' ' + styles.selectedItem : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSelect(army.army)}
                        >
                          {army.name}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ArmyComboBox; 